/**
 * PatientSearchModal — full-screen search overlay invoked from the
 * Patient tab. Sprint K-Recall.
 *
 * Lets the EMT search the agency's patient registry by name, DOB,
 * phone, or government-ID number. Each result row shows the
 * patient's last-seen-at and encounter count so the EMT can quickly
 * spot the right record (vs. a name collision). Tap a result to
 * recall: pulls demographics + history + allergies + meds back into
 * the current ePCR and surfaces any active danger flags.
 */

import { useEffect, useMemo, useState } from 'react';
import { Loader2, Search, X, AlertTriangle, Activity, Calendar } from 'lucide-react';
import {
  searchPatients,
  recallPatient,
  patientToEpcrPatch,
  DANGER_FLAG_LABEL,
  DANGER_FLAG_TONE,
  type RegistryPatient,
  type PatientDangerFlag,
  type RecallResult,
} from '@/lib/patient-registry';
import { logEpcrAccess } from '@/lib/epcr-audit';
import type { EPCR } from '@/types';
import { cn } from '@/lib/cn';

interface Props {
  epcr: EPCR;
  onApply: (updates: Partial<EPCR>, flags: PatientDangerFlag[]) => void;
  onClose: () => void;
}

const TONE_PILL: Record<string, string> = {
  red:   'bg-red-100 text-red-800 border-red-300',
  amber: 'bg-amber-100 text-amber-900 border-amber-300',
  blue:  'bg-blue-100 text-blue-800 border-blue-300',
  slate: 'bg-slate-100 text-slate-800 border-slate-300',
};

export function PatientSearchModal({ epcr, onApply, onClose }: Props) {
  const [lastName, setLastName]   = useState(epcr.patient_last_name ?? '');
  const [firstName, setFirstName] = useState(epcr.patient_first_name ?? '');
  const [dob, setDob]             = useState(epcr.patient_dob ?? '');
  const [results, setResults] = useState<RegistryPatient[] | null>(null);
  const [busy, setBusy]       = useState(false);
  const [recalling, setRecalling] = useState<string | null>(null);
  const [error, setError]     = useState<string | null>(null);

  // ESC closes; lock body scroll while open.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [onClose]);

  const canSearch = useMemo(() => {
    return lastName.trim().length >= 2 || dob.length === 10 || firstName.trim().length >= 2;
  }, [lastName, firstName, dob]);

  async function runSearch() {
    setBusy(true);
    setError(null);
    try {
      const r = await searchPatients({
        lastName: lastName || undefined,
        firstName: firstName || undefined,
        dob: dob || undefined,
      });
      setResults(r);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Search failed.');
    } finally {
      setBusy(false);
    }
  }

  async function recall(patientId: string) {
    setRecalling(patientId);
    setError(null);
    try {
      const result: RecallResult | null = await recallPatient(patientId);
      if (!result) {
        setError('Could not load patient. Permission or network issue.');
        return;
      }
      const patch = patientToEpcrPatch(result.patient);
      onApply(patch, result.flags);
      // Stamp the recall on the ePCR access log.
      logEpcrAccess(epcr.id, 'edit_field', { source: 'patient_recall', patient_id: patientId });
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Recall failed.');
    } finally {
      setRecalling(null);
    }
  }

  return (
    <div
      role="dialog"
      aria-modal="true"
      onClick={onClose}
      className="fixed inset-0 z-50 flex items-start justify-center bg-slate-950/70 p-4 overflow-auto"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="bg-white rounded-lg shadow-2xl max-w-3xl w-full my-8 relative"
      >
        <button
          onClick={onClose}
          aria-label="Close"
          className="absolute top-2 right-2 z-10 rounded-full p-1.5 text-slate-500 hover:bg-slate-100 hover:text-slate-900 min-h-touch min-w-touch flex items-center justify-center"
        >
          <X size={18} />
        </button>

        <header className="border-b border-slate-200 px-5 py-3">
          <h2 className="text-base font-bold text-slate-900 flex items-center gap-2">
            <Search size={18} className="text-blue-700" />
            Recall existing patient
          </h2>
          <p className="text-xs text-slate-600 mt-0.5">
            Search the agency's patient registry. Selecting a result pulls
            demographics, medical history, allergies, home meds, and any
            active danger flags into this ePCR.
          </p>
        </header>

        {/* Search form */}
        <div className="px-5 py-4 space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">Last name</span>
              <input
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canSearch) runSearch(); }}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
                autoFocus
                autoComplete="family-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">First name</span>
              <input
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canSearch) runSearch(); }}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
                autoComplete="given-name"
              />
            </label>
            <label className="block">
              <span className="text-[10px] font-mono uppercase tracking-widest text-slate-500">DOB</span>
              <input
                type="date"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                onKeyDown={(e) => { if (e.key === 'Enter' && canSearch) runSearch(); }}
                className="mt-1 w-full rounded border border-slate-300 px-3 py-2 text-sm min-h-touch focus:border-blue-500 focus:outline-none"
              />
            </label>
          </div>
          <div className="flex items-center gap-3">
            <button
              onClick={runSearch}
              disabled={!canSearch || busy}
              className="rounded-md bg-blue-700 hover:bg-blue-800 disabled:bg-slate-300 disabled:cursor-not-allowed px-4 py-2 text-sm font-semibold text-white min-h-touch flex items-center gap-2"
            >
              {busy ? <><Loader2 size={14} className="animate-spin" /> Searching…</> : <><Search size={14} /> Search</>}
            </button>
            <span className="text-[11px] text-slate-500">
              At least 2 characters of name OR a full DOB.
            </span>
          </div>

          {error && (
            <div className="border border-red-300 bg-red-50 rounded px-3 py-2 text-sm text-red-900">
              {error}
            </div>
          )}
        </div>

        {/* Results */}
        <div className="border-t border-slate-200 max-h-[60vh] overflow-y-auto">
          {results === null ? (
            <p className="text-xs text-slate-500 italic px-5 py-6 text-center">
              No search yet. Enter a name fragment or DOB and tap Search.
            </p>
          ) : results.length === 0 ? (
            <p className="text-xs text-slate-500 italic px-5 py-6 text-center">
              No matches. New patient — fill the demographics tab as usual; the
              registry will pick up this record when the ePCR is locked.
            </p>
          ) : (
            <ul className="divide-y divide-slate-200">
              {results.map((p) => (
                <li key={p.id}>
                  <button
                    onClick={() => recall(p.id)}
                    disabled={recalling === p.id}
                    className="w-full text-left px-5 py-3 hover:bg-slate-50 disabled:opacity-60 flex items-start gap-3"
                  >
                    <div className="flex-1 min-w-0">
                      <div className="flex items-baseline gap-2 flex-wrap">
                        <span className="font-bold text-slate-900">
                          {p.last_name ?? '—'}
                          {p.first_name ? `, ${p.first_name}` : ''}
                          {p.middle_name ? ` ${p.middle_name[0]}` : ''}
                        </span>
                        {p.gender && <span className="text-xs text-slate-500">· {p.gender}</span>}
                        {p.dob && (
                          <span className="text-xs text-slate-500 inline-flex items-center gap-1">
                            <Calendar size={10} /> {p.dob}
                          </span>
                        )}
                      </div>
                      <div className="text-[11px] text-slate-600 mt-0.5">
                        {[p.address, p.city, p.state, p.zip].filter(Boolean).join(', ')}
                      </div>
                      <div className="mt-1 flex flex-wrap items-center gap-3 text-[11px] text-slate-500">
                        <span className="inline-flex items-center gap-1">
                          <Activity size={10} />
                          {p.encounter_count} encounter{p.encounter_count === 1 ? '' : 's'}
                        </span>
                        {p.last_seen_at && (
                          <span>last seen {new Date(p.last_seen_at).toLocaleDateString()}</span>
                        )}
                        {p.medical_history?.length > 0 && (
                          <span>{p.medical_history.length} hx</span>
                        )}
                        {Array.isArray(p.allergies) && p.allergies.length > 0 && (
                          <span className="text-amber-700">{p.allergies.length} allergy{p.allergies.length === 1 ? '' : 'ies'}</span>
                        )}
                      </div>
                    </div>
                    {recalling === p.id ? (
                      <Loader2 size={16} className="animate-spin text-slate-500 mt-1" />
                    ) : (
                      <span className="text-xs text-blue-700 font-semibold whitespace-nowrap mt-1">
                        Recall →
                      </span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <footer className="border-t border-slate-200 px-5 py-2 text-[11px] text-slate-500">
          Patient access is HIPAA-logged.
        </footer>
      </div>
    </div>
  );
}

/**
 * Inline danger-flag banner for the ePCR header. Renders the flags
 * returned alongside a recalled patient. Persistent across the call;
 * cannot be dismissed during the run (per Addendum §5.4).
 */
export function DangerFlagBanner({ flags }: { flags: PatientDangerFlag[] }) {
  if (!flags.length) return null;
  return (
    <div className="border-2 border-red-400 bg-red-50 rounded-lg p-3 space-y-1.5">
      <div className="flex items-center gap-2 text-red-900 font-bold text-sm">
        <AlertTriangle size={16} />
        Patient flags from prior encounters
      </div>
      <div className="flex flex-wrap gap-1.5">
        {flags.map((f) => (
          <span
            key={f.id}
            title={f.notes ?? DANGER_FLAG_LABEL[f.category]}
            className={cn(
              'inline-flex items-center gap-1 px-2 py-0.5 rounded border text-[11px] font-bold',
              TONE_PILL[DANGER_FLAG_TONE[f.category]],
            )}
          >
            {DANGER_FLAG_LABEL[f.category]}
            {f.severity && <span className="opacity-60">({f.severity})</span>}
            {f.status === 'candidate' && (
              <span className="opacity-60 italic">unconfirmed</span>
            )}
          </span>
        ))}
      </div>
    </div>
  );
}
